if (!customElements.get('header-drawer-menu-options')) {
  customElements.define('header-drawer-menu-options', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { capture: true, signal: this.controller.signal };
      this.addEventListener('click', (event) => {
        if (this.dataset.mode !== 'accordion') return;
        const summary = event.target.closest('summary');
        if (!summary || !this.contains(summary)) return;
        const details = summary.parentElement;
        if (details.tagName !== 'DETAILS') return;
        event.preventDefault();
        event.stopPropagation();
        details.open = !details.open;
        summary.setAttribute('aria-expanded', String(details.open));
      }, options);
      this.addEventListener('keyup', (event) => {
        if (this.dataset.mode !== 'accordion' || event.code !== 'Escape') return;
        const details = event.target.closest('details[open]');
        if (!details || !this.contains(details)) return;
        event.stopPropagation();
        details.open = false;
        const summary = details.querySelector(':scope > summary');
        summary.setAttribute('aria-expanded', 'false');
        summary.focus();
      }, options);
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}

// Keep other header blocks on their existing route. Only direct native collapsible
// rows leave the inert collection; nested children travel with their parent.
if (!customElements.get('db-header-extra')) {
  customElements.define('db-header-extra', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) return;
        const source = this.querySelector('template');
        const drawer = this.closest('header-drawer');
        if (!source || !drawer) return;
        const nativeUtilities = Array.from(source.content.querySelectorAll('[data-db-header-utility]'));
        const nativeMenus = Array.from(source.content.querySelectorAll('[data-db-header-frame="menu"]'));
        const frames = Array.from(source.content.children).filter(row => row.matches('[data-db-header-frame]'));
        const inner = drawer.querySelector('.menu-drawer__inner-container');
        const menu = drawer.querySelector('#menu-drawer');
        if (inner && menu) {
          // Body replacement may detach the legacy utility/footer slot before Footer is mounted.
          const existingUtility = inner.querySelector('.menu-drawer__utility-links');
          for (const frame of frames) {
            const kind = frame.dataset.dbHeaderFrame;
            if (kind === 'menu') continue;
            if (kind === 'root') {
              menu.classList.toggle('is-body-scroll', frame.dataset.sticky === 'true');
              continue;
            }
            const style = frame.previousElementSibling;
            if (style?.tagName === 'STYLE' && style.textContent.includes(`#${frame.id}`)) inner.append(style);
            frame.remove();
            if (kind === 'body') {
              const oldBody = inner.querySelector('.header-drawer-v2__body');
              const navigation = oldBody?.querySelector('.menu-drawer__navigation-container');
              if (navigation) frame.prepend(navigation);
              if (oldBody) oldBody.replaceWith(frame);
              else inner.append(frame);
            } else if (kind === 'header') {
              inner.querySelector('.header-drawer-v2__header')?.remove();
              inner.prepend(frame);
              frame.querySelector('.menu-drawer__close-menu-btn')?.addEventListener('click', event => {
                drawer.closeMenuDrawer(event, drawer.mainDetailsToggle.querySelector('summary'));
              });
            } else if (kind === 'footer') {
              const utility = existingUtility;
              if (utility) frame.append(utility);
              const oldFooter = inner.querySelector('.header-drawer-v2__footer');
              if (oldFooter) oldFooter.replaceWith(frame);
              else inner.append(frame);
            }
          }
          const fallback = drawer.querySelector('.menu-drawer__navigation-container nav');
          const anchor = fallback?.closest('header-drawer-menu-options') || fallback;
          let previousMenu;
          for (const nativeMenu of nativeMenus) {
            const nestedBody = nativeMenu.closest('[data-db-header-frame="body"]');
            const style = nativeMenu.previousElementSibling;
            if (style?.tagName === 'STYLE' && style.textContent.includes(`#${nativeMenu.id}`)) {
              if (nestedBody) nativeMenu.before(style);
              else if (anchor?.isConnected) anchor.before(style);
              else if (previousMenu) previousMenu.after(style);
            }
            if (nestedBody) {
              anchor?.remove();
              const before = drawer.querySelector('[data-db-header-slot="before_menu"]');
              const after = drawer.querySelector('[data-db-header-slot="after_menu"]');
              if (before) nativeMenu.before(before);
              if (after) nativeMenu.after(after);
            } else {
              if (anchor?.isConnected) anchor.replaceWith(nativeMenu);
              else if (previousMenu) previousMenu.after(nativeMenu);
              else drawer.querySelector('.header-drawer-v2__body')?.append(nativeMenu);
            }
            previousMenu = nativeMenu;
            nativeMenu.querySelectorAll('summary').forEach(summary => {
              summary.setAttribute('aria-expanded', String(summary.parentElement.open));
              summary.addEventListener('click', drawer.onSummaryClick.bind(drawer));
            });
            nativeMenu.querySelectorAll('.menu-drawer__close-button').forEach(button => {
              button.addEventListener('click', drawer.onCloseButtonClick.bind(drawer));
            });
          }
          for (const utility of nativeUtilities) {
            const kind = utility.dataset.dbHeaderUtility;
            if (kind === 'account') {
              drawer.querySelectorAll('.menu-drawer__utility-links > .menu-drawer__account').forEach(node => node.remove());
            } else if (kind === 'localization') {
              drawer.querySelectorAll('.menu-drawer__utility-links > .menu-drawer__localization').forEach(node => node.remove());
            }
            if (source.content.contains(utility)) {
              (drawer.querySelector('[data-db-header-slot="footer"]') || drawer.querySelector('.header-drawer-v2__footer'))?.append(utility);
            }
          }
          const root = frames.find(frame => frame.dataset.dbHeaderFrame === 'root');
          if (root?.dataset.designMode === 'true' && root.dataset.testMode === 'true' && !drawer.mainDetailsToggle.open) {
            drawer.mainDetailsToggle.open = true;
            drawer.openMenuDrawer(drawer.mainDetailsToggle.querySelector('summary'));
          }
        }
        for (const row of Array.from(source.content.children)) {
          if (!row.matches('[data-db-header-position]')) continue;
          const position = row.dataset.dbHeaderPosition;
          if (!['before_menu', 'after_menu', 'footer'].includes(position)) continue;
          const slot = drawer.querySelector(`[data-db-header-slot="${position}"]`);
          if (!slot) continue;
          // The native tag:null block emits its scoped style just before its root.
          const style = row.previousElementSibling;
          if (style?.tagName === 'STYLE' && style.textContent.includes(`#${row.dataset.dbHeaderStyleId || row.id}`)) slot.append(style);
          slot.append(row);
          const details = row.querySelector(':scope > details');
          const summary = details?.querySelector(':scope > summary');
          if (summary) {
            const syncExpanded = () => summary.setAttribute('aria-expanded', String(details.open));
            details.addEventListener('toggle', syncExpanded);
            syncExpanded();
          }
        }
      });
    }
  });
}
